import { useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";

const BeforeAfter = ({ beforeImg, afterImg, beforeLabel = "Prima", afterLabel = "Dopo" }) => {
  const ref = useRef(null);
  const overlayRef = useRef(null);
  const handleRef = useRef(null);
  const posRef = useRef(50);
  const dragging = useRef(false);
  const [ariaPos, setAriaPos] = useState(50);

  const apply = (pct) => {
    posRef.current = pct;
    if (overlayRef.current) overlayRef.current.style.clipPath = `inset(0 0 0 ${pct}%)`;
    if (handleRef.current) handleRef.current.style.left = `${pct}%`;
  };

  const updateFromX = (clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    apply(Math.min(96, Math.max(4, pct)));
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    try {
      ref.current?.setPointerCapture(e.pointerId);
    } catch (err) {}
    updateFromX(e.clientX);
  };

  const onPointerMove = (e) => {
    if (dragging.current) updateFromX(e.clientX);
  };

  const stop = () => {
    if (dragging.current) setAriaPos(Math.round(posRef.current));
    dragging.current = false;
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      data-lenis-prevent
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
      className="relative w-full aspect-[4/3] md:aspect-[16/10] overflow-hidden select-none cursor-ew-resize bg-sand"
      style={{ touchAction: "none" }}
      role="slider"
      aria-label="Confronto prima e dopo la ristrutturazione"
      aria-valuenow={ariaPos}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
          const next = Math.min(96, Math.max(4, posRef.current + (e.key === "ArrowRight" ? 4 : -4)));
          apply(next);
          setAriaPos(Math.round(next));
          e.preventDefault();
        }
      }}
    >
      <img
        src={beforeImg}
        alt="Ambiente prima della ristrutturazione"
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        draggable={false}
      />
      <div
        ref={overlayRef}
        className="absolute inset-0 will-change-[clip-path]"
        style={{ clipPath: "inset(0 0 0 50%)" }}
      >
        <img
          src={afterImg}
          alt="Ambiente dopo la ristrutturazione"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          draggable={false}
        />
      </div>

      <span data-testid="before-label" className="absolute top-4 left-4 bg-carbone/70 backdrop-blur-sm text-ivory font-display text-[11px] tracking-[0.25em] uppercase px-3 py-1.5 pointer-events-none">
        {beforeLabel}
      </span>
      <span data-testid="after-label" className="absolute top-4 right-4 bg-terracotta/90 backdrop-blur-sm text-ivory font-display text-[11px] tracking-[0.25em] uppercase px-3 py-1.5 pointer-events-none">
        {afterLabel}
      </span>

      <div
        ref={handleRef}
        data-testid="before-after-handle"
        className="absolute top-0 bottom-0 w-px bg-ivory"
        style={{ left: "50%" }}
      >
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 bg-ivory text-anthracite flex items-center justify-center shadow-md">
          <ChevronsLeftRight size={18} strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
};

export default BeforeAfter;
